/**
 * Dev Mock Layer — 为离线开发测试账户提供模拟 API 响应
 *
 * 问题背景：
 *   离线/测试账户使用伪造 token (offline_*, dev_*)，被真实网关拒绝返回 401。
 *   导致所有 API 调用失败 → 界面空白、对话框不显示、菜单模块不加载。
 *
 * 方案：
 *   拦截 dev token 的 fetch 请求，返回模拟数据，绕过网关认证。
 *   仅在 import.meta.env.DEV 时生效。
 */

interface MockHandler {
    match: (url: string, method: string) => boolean;
    respond: (url: string, init: RequestInit, bodyText: string) => Response | Promise<Response>;
}

const isDevMode = (): boolean => {
    try {
        return !!(import.meta as any).env?.DEV;
    } catch {
        return false;
    }
};

const isDevToken = (): boolean => {
    if (!isDevMode()) return false;
    try {
        const token = localStorage.getItem('auth_token') || '';
        return token.startsWith('offline_') || token.startsWith('dev_') || token.startsWith('mock_token_');
    } catch {
        return false;
    }
};

export function shouldMock(): boolean {
    // [P2] VITE_OFFLINE=1 离线开发模式时启用 DevMock；生产/预发保持强制禁用。
    try {
        if ((import.meta as any).env?.VITE_OFFLINE === '1') return true;
    } catch { }
    return false; // Force disable all Dev Mock interference in production and staging
}

/**
 * [FIX] 初始化 Dev Mock 环境
 * 设置 localStorage 中的投影 (projection) 数据和授权信息，
 * 解决 AuthBoundary 在所有模块视图中显示黑框拦截层的问题。
 *
 * 原理：AuthBoundary 通过两个途径判断访问权限：
 *   1. grantedBundles (imStore state) — 本次修复在 imStore.ts 中处理
 *   2. projection_effective (localStorage) — 本次修复在此初始化
 *
 * 必须在应用启动时调用，确保在 AuthBoundary 计算 access 之前完成注入。
 */
export function initDevMockEnv(): void {
    if (!isDevToken()) return;
    try {
        const profileId = 'dev_profile';
        const now = Date.now();

        // 投影 effective 数据 — 授予所有模块 local 访问权限
        const projectionEffective = {
            profile_id: profileId,
            epoch: 1,
            plane: 'dev',
            permissions: [
                'commerce:read', 'commerce:write', 'commerce:admin',
                'pricing:read', 'pricing:write',
                'content:read', 'content:write',
                'social:read', 'social:write',
                'ad:read', 'ad:write', 'ad:admin',
                'ads.manage', 'ads.view',
                'director:view', 'director:edit', 'director:render', 'director:export', 'director:admin',
                'topology:edit', 'immune:audit', 'workflow:dispatch',
            ],
            local_commands: ['*'],
            ops_handover_commands: [],
            denied_commands: [],
            ui_routes: [
                '/', '/pro-nexus', '/square', '/matrix', '/board',
                '/director', '/admin', '/settings', '/user',
                '/account-matrix', '/commerce-ops', '/ad-platform',
                '/darwin', '/creative', '/beehive', '/medical', '/eternity',
                '/morphic', '/fleet', '/storage', '/starport',
                '/edge-console', '/mining', '/cerebral', '/social-ops',
                '/payloads', '/topology', '/sovereign', '/sovereign-matrix',
                '/tactical-mesh', '/system-console', '/ai-command',
                '/authz-panel', '/bridge', '/ops', '/eye', '/evolution',
                '/synapse', '/phantom', '/browser',
                '/security/gate', '/login', '/register',
                '/world-monitor', '/divination',
            ],
            modules: [
                { module_id: 'commerce_operations_center', delivery_mode: 'local', risk_tier: 'low', ui_routes: ['/commerce-ops'] },
                { module_id: 'content_command_center', delivery_mode: 'local', risk_tier: 'low', ui_routes: ['/account-matrix'] },
                { module_id: 'ad_platform', delivery_mode: 'local', risk_tier: 'low', ui_routes: ['/ad-platform'] },
                { module_id: 'director_dashboard', delivery_mode: 'local', risk_tier: 'low', ui_routes: ['/director'] },
                { module_id: 'ecommerce', delivery_mode: 'local', risk_tier: 'low', ui_routes: ['/commerce-ops', '/ad-platform'] },
                { module_id: 'darwin', delivery_mode: 'local', risk_tier: 'low', ui_routes: ['/darwin'] },
                { module_id: 'authz_panel', delivery_mode: 'local', risk_tier: 'low', ui_routes: ['/authz-panel'] },
                { module_id: 'bridge', delivery_mode: 'local', risk_tier: 'low', ui_routes: ['/bridge'] },
            ],
        };

        // 写入 localStorage
        window.localStorage.setItem('123all_projection_effective', JSON.stringify(projectionEffective));
        window.localStorage.setItem('123all_projection_ui_routes', JSON.stringify(projectionEffective.ui_routes));
        window.localStorage.setItem('active_profile', profileId);
        // 禁用路由守卫的投影限制 (dev 模式下不需要)
        window.localStorage.setItem('123all_projection_route_guard', 'false');

        // 允许开发人员默认启用真实大模型（跳过前端的 Mock 聊天拦截）
        if (window.localStorage.getItem('bypass_llm_mock') === null) {
            window.localStorage.setItem('bypass_llm_mock', 'true');
        }

        // [2026-09-25] 原来这里派发 '123all:projection-updated'「通知 AuthBoundary 重新计算」——
        // 但 AuthBoundary 的整套投影机制（含该事件监听）在 2026-09-23 W11-F/IC-56 被**刻意删除**
        //（它曾是常量 true 的假边界；真边界在服务端与路由守卫）。派发指向已删机制，删除。

        // 劫持全局 fetch 以支持全系统 API Mock (如 Beehive Swarm Direct fetch)
        if (typeof window !== 'undefined' && !((window as any).__MOCKED_FETCH__)) {
            const originalFetch = window.fetch;
            window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
                const mockRes = await devMockFetch(input, init);
                if (mockRes) return mockRes;
                return originalFetch(input, init);
            };
            (window as any).__MOCKED_FETCH__ = true;
            console.log('[DevMock] Global fetch successfully intercepted.');
        }

        console.log('[DevMock] Dev environment initialized — projection effective, ui_routes, active_profile set.');
    } catch (e) {
        console.error('[DevMock] Failed to initialize dev environment:', e);
    }
}

/** 生成模拟聊天流式 SSE 响应 */
function createStreamResponse(prompt: string): Response {
    const mockContent = `您好！我是 Nym AI Assistant。

您刚才的问题是："${prompt.slice(0, 60)}${prompt.length > 60 ? '...' : ''}"

这是开发环境（DEV Mock）模拟响应。在真实部署中，此 API 将由后端大模型引擎处理。
当前使用离线开发账户进行测试，所有 API 请求已被前端 Mock 层拦截。

功能一览：
• 代码分析与架构 — 深度排查 Bug，优化逻辑并重构复杂系统代码
• 知识提取与综述 — 结合端云算力，极速总结长文本与跨语种资料
• 视觉创作与渲染 — 唤醒内置绘画引擎，即时生成并编辑高精度图像
• 系统规划 — 制定详细的系统升级计划

请通过左侧菜单栏探索更多功能模块。`;

    const encoder = new TextEncoder();
    const chunks = [
        { id: `chatcmpl-mock-${Date.now()}`, object: 'chat.completion.chunk', created: Math.floor(Date.now() / 1000), model: 'nym-core-v3', choices: [{ index: 0, delta: { role: 'assistant', content: '' }, finish_reason: null }] },
        ...mockContent.match(/.{1,20}/g)?.map((chunk, i, arr) => ({
            id: `chatcmpl-mock-${Date.now()}`,
            object: 'chat.completion.chunk',
            created: Math.floor(Date.now() / 1000),
            model: 'nym-core-v3',
            choices: [{ index: 0, delta: { content: chunk }, finish_reason: i === arr.length - 1 ? 'stop' : null }]
        })) || [],
    ];

    let index = 0;
    const stream = new ReadableStream({
        start(controller) {
            const push = () => {
                if (index < chunks.length) {
                    const data = `data: ${JSON.stringify(chunks[index])}\n\n`;
                    controller.enqueue(encoder.encode(data));
                    index++;
                    setTimeout(push, 30 + Math.random() * 40);
                } else {
                    controller.enqueue(encoder.encode('data: [DONE]\n\n'));
                    controller.close();
                }
            };
            push();
        }
    });

    return new Response(stream, {
        status: 200,
        headers: {
            'Content-Type': 'text/event-stream',
            'Cache-Control': 'no-cache',
            'Connection': 'keep-alive',
        }
    });
}

/** 生成模拟非流式聊天响应 */
function createNonStreamResponse(prompt: string): Response {
    const content = `您好！这是开发环境（DEV Mock）模拟响应。

您的问题："${prompt.slice(0, 80)}${prompt.length > 80 ? '...' : ''}"

当前使用离线开发账户，API 请求由前端 Mock 层拦截。
在真实部署中，此响应将由后端大模型引擎处理。`;

    return new Response(JSON.stringify({
        id: `chatcmpl-mock-${Date.now()}`,
        object: 'chat.completion',
        created: Math.floor(Date.now() / 1000),
        model: 'nym-core-v3',
        choices: [{
            index: 0,
            message: { role: 'assistant', content },
            finish_reason: 'stop'
        }],
        usage: { prompt_tokens: prompt.length, completion_tokens: content.length, total_tokens: prompt.length + content.length }
    }), {
        status: 200,
        headers: { 'Content-Type': 'application/json' }
    });
}

/** Mock handlers 路由 */
const mockHandlers: MockHandler[] = [
    // Chat completions
    {
        match: (url) => url.includes('/v1/chat/completions'),
        respond: (_url, _init, bodyText) => {
            let prompt = 'Hello';
            let isStream = false;
            try {
                const body = JSON.parse(bodyText);
                prompt = body?.messages?.[body.messages.length - 1]?.content || prompt;
                isStream = body?.stream === true;
            } catch { /* use default */ }
            return isStream ? createStreamResponse(prompt) : createNonStreamResponse(prompt);
        }
    },
    // User profile
    {
        match: (url) => url.includes('/v1/user/profile') || url.includes('/v1/users/me'),
        respond: () => new Response(JSON.stringify({
            id: 'user_mock_dev',
            email: 'dev@antigravity.ai',
            displayName: 'Dev Commander',
            avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=DevCommander'
        }), { status: 200, headers: { 'Content-Type': 'application/json' } })
    },
    // Subscription
    {
        match: (url) => url.includes('/v1/subscription') || url.includes('/v1/billing/subscription'),
        respond: () => new Response(JSON.stringify({
            tier: 'PRO',
            status: 'active' as const,
            currentPeriodEnd: '2026-12-31',
            features: ['basic', 'advanced', 'priority_support', 'api_access'],
            limits: { maxModelSizeGB: 100, maxConcurrentInferences: 10, apiRateLimit: 1000 }
        }), { status: 200, headers: { 'Content-Type': 'application/json' } })
    },
    // Balance
    {
        match: (url) => url.includes('/v1/balance') || url.includes('/v1/billing/balance'),
        respond: () => new Response(JSON.stringify({
            available: 1250.50, frozen: 0.0, currency: 'USD'
        }), { status: 200, headers: { 'Content-Type': 'application/json' } })
    },
    // Health check
    {
        match: (url) => url.includes('/v1/system/health') || url.includes('/v1/health'),
        respond: () => new Response(JSON.stringify({
            status: 'ok', mode: 'dev-mock', version: 'v23-dev'
        }), { status: 200, headers: { 'Content-Type': 'application/json' } })
    },
    // Mock /authz/manifest
    {
        match: (url, method) => url.includes('/api/v1/authz/manifest') && method === 'GET',
        respond: () => {
            return new Response(JSON.stringify({
                version: 'dev_mock_fallback',
                signature: 'zkp_0000000000000000000000000000000000000000000000000000000000000000',
                routes: [
                    '/director', '/board', '/matrix', '/federation', 
                    '/security/gate', '/tianshu', '/medical', '/phantom', '/rbac'
                ]
            }), { status: 200, headers: { 'Content-Type': 'application/json' } });
        }
    },
    // Mock /authz/monitor/update_stage
    {
        match: (url, method) => url.includes('/authz/monitor/update_stage') && method === 'POST',
        respond: () => {
            const projectionEffective = JSON.parse(localStorage.getItem('123all_projection_effective') || '{}');
            localStorage.setItem('123all_projection_effective', JSON.stringify({ ...projectionEffective, aios_stage: 'II_CIRCULATION' }));
            return new Response(JSON.stringify({
                ok: true,
                message: 'Stage updated to II_CIRCULATION'
            }), { status: 200, headers: { 'Content-Type': 'application/json' } });
        }
    },
    // Projection effective — 关键：防 AuthBoundary 黑框拦截
    {
        match: (url) => url.includes('/api/v1/ops/projection/effective'),
        respond: () => {
            const projectionEffective = JSON.parse(
                localStorage.getItem('123all_projection_effective') || 'null'
            );
            return new Response(JSON.stringify({
                success: true,
                effective: projectionEffective || {
                    profile_id: 'dev_profile',
                    epoch: 1,
                    plane: 'dev',
                    permissions: ['commerce:read', 'pricing:read'],
                    local_commands: ['*'],
                    modules: [
                        { module_id: 'commerce_operations_center', delivery_mode: 'local' },
                        { module_id: 'content_command_center', delivery_mode: 'local' },
                        { module_id: 'ad_platform', delivery_mode: 'local' },
                    ],
                }
            }), { status: 200, headers: { 'Content-Type': 'application/json' } });
        }
    },

    // Device Hub
    {
        match: (url) => url.includes('/v1/device-hub/my-devices'),
        respond: () => new Response(JSON.stringify({
            devices: [
                {
                    device_id: 'dev_mock_desktop_01',
                    device_name: 'Dev Desktop Client',
                    device_type: 'native_desktop',
                    is_online: true,
                    os_info: 'mock-os',
                    capabilities: ['local_compute', 'hardware_accelerated'],
                    last_heartbeat: Date.now()
                }
            ]
        }), { status: 200, headers: { 'Content-Type': 'application/json' } })
    },
    // Beehive Blueprints GET List
    {
        match: (url, method) => url.includes('/api/v1/tools/blueprints') && method === 'GET',
        respond: () => {
            const saved = localStorage.getItem('mock_beehive_blueprints');
            let blueprints = [];
            if (saved) {
                try { blueprints = JSON.parse(saved); } catch {}
            }
            if (!blueprints || blueprints.length === 0) {
                blueprints = [
                    {
                        tool_id: 'github',
                        display_name: 'GitHub Connector',
                        icon_key: 'github',
                        connection_type: { type: 'OAuth2', params: {} },
                        is_connected: true,
                        fields: []
                    },
                    {
                        tool_id: 'openai',
                        display_name: 'OpenAI Swarm',
                        icon_key: 'openai',
                        connection_type: 'ApiKey',
                        is_connected: false,
                        fields: [
                            { key: 'api_key', label: 'API Key', field_type: 'password', placeholder: 'sk-...', required: true },
                            { key: 'org_id', label: 'Organization ID', field_type: 'text', placeholder: 'org-...', required: false }
                        ]
                    },
                    {
                        tool_id: 'anthropic',
                        display_name: 'Anthropic Core',
                        icon_key: 'anthropic',
                        connection_type: 'ApiKey',
                        is_connected: false,
                        fields: [
                            { key: 'api_key', label: 'API Key', field_type: 'password', placeholder: 'sk-ant-...', required: true }
                        ]
                    },
                    {
                        tool_id: 'google',
                        display_name: 'Google Gemini Pro',
                        icon_key: 'google',
                        connection_type: { type: 'OAuth2', params: {} },
                        is_connected: false,
                        fields: []
                    },
                    {
                        tool_id: 'webhook_dispatch',
                        display_name: 'Custom Webhook',
                        icon_key: 'amp',
                        connection_type: 'Webhook',
                        is_connected: false,
                        fields: [
                            { key: 'webhook_url', label: 'Webhook URL', field_type: 'text', placeholder: 'https://...', required: true },
                            { key: 'secret_key', label: 'Secret Key', field_type: 'password', placeholder: 'Sign secret', required: false }
                        ]
                    }
                ];
                localStorage.setItem('mock_beehive_blueprints', JSON.stringify(blueprints));
            }
            return new Response(JSON.stringify(blueprints), { status: 200, headers: { 'Content-Type': 'application/json' } });
        }
    },
    // Beehive Connect POST
    {
        match: (url, method) => url.includes('/api/v1/tools/blueprints/connect') && method === 'POST',
        respond: (_url, _init, bodyText) => {
            let toolId = '';
            try {
                const body = JSON.parse(bodyText);
                toolId = body?.tool_id || '';
            } catch {}

            const saved = localStorage.getItem('mock_beehive_blueprints');
            let blueprints = [];
            if (saved) {
                try { blueprints = JSON.parse(saved); } catch {}
            }
            if (blueprints && blueprints.length > 0 && toolId) {
                blueprints = blueprints.map((bp: any) => {
                    if (bp.tool_id === toolId) {
                        return { ...bp, is_connected: true };
                    }
                    return bp;
                });
                localStorage.setItem('mock_beehive_blueprints', JSON.stringify(blueprints));
            }
            return new Response(JSON.stringify({ success: true, message: `Connected to ${toolId}` }), { status: 200, headers: { 'Content-Type': 'application/json' } });
        }
    },
    // Beehive Disconnect POST
    {
        match: (url, method) => url.includes('/api/v1/tools/blueprints/') && url.includes('/disconnect') && method === 'POST',
        respond: (url) => {
            // Extract tool_id from url: e.g. /api/v1/tools/blueprints/openai/disconnect
            const parts = url.split('/');
            const idx = parts.findIndex(p => p === 'blueprints');
            const toolId = idx !== -1 ? parts[idx + 1] : '';

            const saved = localStorage.getItem('mock_beehive_blueprints');
            let blueprints = [];
            if (saved) {
                try { blueprints = JSON.parse(saved); } catch {}
            }
            if (blueprints && blueprints.length > 0 && toolId) {
                blueprints = blueprints.map((bp: any) => {
                    if (bp.tool_id === toolId) {
                        return { ...bp, is_connected: false };
                    }
                    return bp;
                });
                localStorage.setItem('mock_beehive_blueprints', JSON.stringify(blueprints));
            }
            return new Response(JSON.stringify({ success: true, message: `Disconnected ${toolId}` }), { status: 200, headers: { 'Content-Type': 'application/json' } });
        }
    },

    // ── 选品工作台（/#/selection）──────────────────────────────────────────
    //
    // 为什么需要显式 handler：此前离线壳对 `/api/v1/selection/*` 走**通用兜底**
    // （`{success:true, mock:true}`），而它的形状不是 `ApiResponse<SelectionCandidate[]>`，
    // 于是模块在离线壳里停在"错误态"——既没法开发，也没法写渲染态判据
    // （e2e 的 `page.route` 打桩会被本文件先一步拦下，根本到不了网络层）。
    // 数据一律标 `radar:synthetic`（合成），**不冒充真实采集**。
    {
        // 失败注入：`localStorage.devmock_selection_fail = '1'` ⇒ 候选列表返回 500。
        // 用途：断言"错误态不伪装成空态"（界面必须显示错误卡 + 重试，而不是"暂无候选品"）。
        match: (url) => url.includes('/api/v1/selection/candidates') && !/\/candidates\/[^/]+/.test(url),
        respond: () => {
            if (localStorage.getItem('devmock_selection_fail') === '1') {
                return new Response(JSON.stringify({ success: false, data: null, error: 'devmock: 注入的候选列表故障' }), { status: 500, headers: { 'Content-Type': 'application/json' } });
            }
            const c = (id: string, title: string, platform: string, priceCents: number, collected: boolean, status: string, risk = 18) => ({
                id,
                original_platform: platform,
                original_item_id: `${id}-item`,
                title,
                category_path: ['家居', '收纳'],
                main_images: [],
                benchmark_price_cents: priceCents,
                estimated_monthly_sales: 1200,
                total_reviews: 240,
                positive_rate: 0.93,
                scores: { blue_ocean_score: 78, burst_velocity: 64, profit_margin_est: 0.41, risk_score: risk, overall_recommendation: 76 },
                voc_insights: {
                    top_defects: [{ category: '做工', percentage: 0.21, severity: 'Medium', sample_quote: '接缝处有毛边（dev 示例）' }],
                    key_selling_points: ['可折叠不占地'],
                    redesign_proposals: ['加厚边角'],
                },
                supply_chain: {
                    supplier_name: '离线示例工厂',
                    source_platform: '1688',
                    source_url: '',
                    unit_cost_cents: 500,
                    moq: 100,
                    lead_time_days: 15,
                    factory_location: 'CN',
                },
                compliance: { is_safe: true, trademark_risk_score: 0.1, patent_risk_score: 0.1, required_certifications: [], risk_keywords_found: [] },
                status,
                data_source: collected ? `collected:${platform}` : 'radar:synthetic',
                discovered_at: Date.now(),
            });
            return new Response(JSON.stringify({
                success: true,
                error: null,
                data: [
                    c('dev-cand-1', '折叠收纳箱 75L（离线示例）', 'tiktok', 1999, true, 'Pending'), // i18n-skip: dev 固件——离线壳示例数据，镜像后端返回形状；生产不加载；界面不渲染后端 label（能力条目名按 id 走词条）
                    // 第二条给**高**风险分：让"风险分越高越危险 ⇒ 红"这条在渲染里真的被走到
                    c('dev-cand-2', '宠物自动喂食器（离线示例）', 'amazon', 2599, false, 'Approved', 72), // i18n-skip: dev 固件——离线壳示例数据，镜像后端返回形状；生产不加载；界面不渲染后端 label（能力条目名按 id 走词条）
                ],
            }), { status: 200, headers: { 'Content-Type': 'application/json' } });
        },
    },
    {
        match: (url) => url.includes('/api/v1/selection/candidates') && /(keywords|compliance|graph)/.test(url),
        respond: (url) => {
            const data = url.includes('/keywords')
                ? { candidate_id: 'dev-cand-1', main_search_intent: '收纳 折叠（dev）', total_traffic_index: 0, top_keywords: [{ keyword: '折叠收纳箱', search_volume_monthly: 0, cpc_bid_cents: 0, competition_ratio: 0, blue_ocean_score: 0, rank_position: 0, measured: false }], negative_keywords: [], traffic_source: 'unavailable' }
                : url.includes('/compliance')
                    ? { candidate_id: 'dev-cand-1', overall_compliance_score: 92, is_passed: true, violations: [], audited_at: Date.now() }
                    : { candidate_id: 'dev-cand-1', root_concept: '收纳', related_concepts: ['可折叠不占地'], competitor_nodes: [], competitor_source: 'not_available', factory_nodes: ['离线示例工厂'], defect_nodes: ['做工'], edges: [] };
            return new Response(JSON.stringify({ success: true, error: null, data }), { status: 200, headers: { 'Content-Type': 'application/json' } });
        },
    },
    {
        // 批量状态写（**必须在单条 /status 之前**：`status-batch` 也含 `/status` 子串）
        match: (url) => url.includes('/api/v1/selection/candidates/status-batch'),
        respond: (_url, _init, bodyText) => {
            let ids: string[] = [];
            try {
                const body = JSON.parse(bodyText || '{}');
                if (Array.isArray(body?.ids)) ids = body.ids;
            } catch { /* 用空数组 */ }
            // 形状与真端点一致：逐条回报 ⇒ 界面只信 applied、skipped 如实进"失败"计数
            return new Response(JSON.stringify({
                success: true,
                error: null,
                data: { applied: ids.length, requested: ids.length, skipped: [], persisted: true },
            }), { status: 200, headers: { 'Content-Type': 'application/json' } });
        },
    },
    {
        match: (url) => url.includes('/api/v1/selection/candidates') && /(status|publish-matrix|export-rpa|simulate-sensitivity)/.test(url),
        respond: (url) => {
            const data = url.includes('publish-matrix')
                ? { candidate_id: 'dev-cand-1', title: '离线示例', target_platforms: ['tiktok'], published_count: 0, published: false, status: 'Pending', ai_creatives_generated: false, per_platform: [{ platform: 'tiktok', display_name: 'TikTok Shop', recognized: true, configured: false, status: 'unavailable:no_credentials', detail: 'dev' }], gate: { decision: 'allow', basis: 'dev', quota_allow_mass_listing: null }, message: 'dev：未发布任何东西' } // i18n-skip: dev 固件——离线壳 publish-matrix 示例响应（镜像后端形状）；生产不加载、界面不从 fixture 取文案
                : url.includes('export-rpa')
                    ? { workflow_id: 'wf_dev', name: 'dev 工作流', description: 'dev', platform: 'tiktok', target_url: 'https://example.com', compiled_steps: [{ step_id: 'step_open_target', verb: { implemented: 'real' } }], required_permission: 'workflow' }
                    : url.includes('simulate-sensitivity')
                        ? { candidate_id: 'dev-cand-1', roas_target: 2, optimal_price_usd: 19.99, points: [{ price_usd: 19.99, estimated_margin_rate: 0.31, breakeven_monthly_units: 42, expected_monthly_profit: 1200.5 }], assumptions: ['需求弹性 -1.5（dev 示例）'] }
                        : { id: 'dev-cand-1', updated: true };
            return new Response(JSON.stringify({ success: true, error: null, data }), { status: 200, headers: { 'Content-Type': 'application/json' } });
        },
    },
    {
        match: (url) => url.includes('/api/v1/selection/capability-status'),
        respond: () => new Response(JSON.stringify({
            success: true,
            error: null,
            data: {
                total: 4,
                available: 1,
                runtime_called: 0,
                runtime_degraded: 0,
                // [A3 保留策略] 与真端点同形状（含**策略取值**：界面不写死 200/7）
                tasks_kept: 3,
                tasks_pruned_total: 12,
                task_retention: { keep: 200, max_age_days: 7 },
                items: [
                    { id: 'radar.tiktok', label: 'TikTok Shop 雷达采集', state: 'credential_gated', source: 'probe' }, // i18n-skip: dev 固件——离线壳示例数据，镜像后端返回形状；生产不加载；界面不渲染后端 label（能力条目名按 id 走词条）
                    { id: 'radar.amazon', label: 'Amazon SP-API 雷达采集', state: 'credential_gated', source: 'probe' }, // i18n-skip: dev 固件——离线壳示例数据，镜像后端返回形状；生产不加载；界面不渲染后端 label（能力条目名按 id 走词条）
                    { id: 'rpa.executor', label: '采集执行体', state: 'not_integrated', source: 'probe' }, // i18n-skip: dev 固件——离线壳示例数据，镜像后端返回形状；生产不加载；界面不渲染后端 label（能力条目名按 id 走词条）
                    { id: 'publish.platforms', label: '平台发布通道', state: 'available', source: 'probe' }, // i18n-skip: dev 固件——离线壳示例数据，镜像后端返回形状；生产不加载；界面不渲染后端 label（能力条目名按 id 走词条）
                ],
            },
        }), { status: 200, headers: { 'Content-Type': 'application/json' } }),
    },
    {
        match: (url) => url.includes('/api/v1/selection/workflows'),
        respond: () => new Response(JSON.stringify({
            success: true,
            error: null,
            data: [{
                id: 'dev-wf', name: '标准选品流程（dev）', description: '离线示例模板', icon: '', tag: 'dev',
                stages: [{ order: 1, name: '雷达采集', stage_type: 'radar', config_summary: 'dev' }], auto_run_rpa: false, required_rbac_perm: 'workflow',
            }],
        }), { status: 200, headers: { 'Content-Type': 'application/json' } }),
    },
    {
        match: (url) => url.includes('/api/v1/selection/tasks'),
        respond: (url) => {
            if (/\/tasks\/[^/]+\/status/.test(url)) {
                return new Response(JSON.stringify({
                    success: true, error: null,
                    data: {
                        task_id: 'dev-task', status: 'Completed',
                        payload: { target_categories: [], target_platforms: [], radar_sources: [], min_price_cents: null, max_price_cents: null, max_competitor_reviews: null, min_monthly_sales: null, workflow_template_id: null, beehive_proxy_group: null },
                        stages: [{ stage_id: 'radar_probe', stage_name: 'radar_probe', status: 'Completed', progress: 100, logs: ['dev 日志'], error_msg: null, rpa_node_id: null }],
                        current_stage_index: 0, created_at: Date.now(), updated_at: Date.now(), candidates_found: 2,
                    },
                }), { status: 200, headers: { 'Content-Type': 'application/json' } });
            }
            return new Response(JSON.stringify({ success: true, error: null, data: { task_id: 'dev-task', status: 'Launched', message: 'dev', metered: false } }), { status: 200, headers: { 'Content-Type': 'application/json' } });
        },
    },
];

/**
 * 拦截 dev token 的 fetch 请求
 * 应在 fetch 调用前使用，替换原生 fetch
 */
export async function devMockFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response | null> {
    if (!shouldMock()) return null;

    const url = typeof input === 'string' ? input : (input instanceof URL ? input.toString() : input.url);
    const method = (init?.method || 'GET').toUpperCase();

    // [TAURI-IPC-SAFEGUARD] Never intercept Tauri native IPC requests
    if (url.startsWith('ipc://') || url.includes('ipc://localhost')) {
        return null;
    }

    // 跳过 auth 端点，让真实登录通过 (但需拦截 authz)
    if (url.includes('/v1/auth/') && !url.includes('/v1/authz/')) return null;
    // [FIX] 跳过网关和模型列表接口，让前端能够读取云端真实模型矩阵
    if (url.includes('/v1/models') || url.includes('/v1/gateway/') || url.includes('/starport/')) return null;
    // [FIX] 如果 localStorage 中设置了 bypass_llm_mock 为 'true'，则跳过对聊天接口的拦截，以实现真实的大模型对话交互
    if (url.includes('/v1/chat/completions') && localStorage.getItem('bypass_llm_mock') === 'true') return null;
    // 跳过 websocket
    if (url.startsWith('ws://') || url.startsWith('wss://')) return null;

    let bodyText = '';
    if (init?.body) {
        if (typeof init.body === 'string') {
            bodyText = init.body;
        } else if (init.body instanceof ArrayBuffer || ArrayBuffer.isView(init.body)) {
            bodyText = new TextDecoder().decode(init.body as BufferSource);
        }
    }

    for (const handler of mockHandlers) {
        if (handler.match(url, method)) {
            console.log(`[DevMock] Intercepted: ${method} ${url.split('?')[0]}`);
            return handler.respond(url, init!, bodyText);
        }
    }

    // Generic mock fallback
    console.log(`[DevMock] Generic mock: ${method} ${url.split('?')[0]}`);
    return new Response(JSON.stringify({
        success: true,
        mock: true,
        path: url.split('?')[0],
        timestamp: Date.now()
    }), { status: 200, headers: { 'Content-Type': 'application/json' } });
}
